import { inject, Injectable } from '@angular/core';
import { FormID } from '@finxone-platform/form-action';
import { UUID } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AlertTypes,
  FormFieldConfig,
  FormWidgetPlacementContext,
  FormsConfigType,
  InputFieldType,
  NestedWorkflowWidgetConfiguration,
  NewWorkflowWidgetConfig,
  NotLayoutWidgetErr,
  ProjectFormsConfig,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
  WorkflowConfig,
  WorkflowPageConfig,
  WorkflowWidgetConfig,
  WorkflowZoneDevicesConfig,
  addWidget,
  findWidget,
  formatNameAsUrl,
  getWidgetsArrayByLayoutTypeOrWidgetId,
} from '@finxone-platform/shared/sys-config-types';
import { findFormRecursive, inputWidgetConfig } from '@finxone-platform/shared/workflow-mapping';
import { getZoneConfig } from '../../utils/workflow-config.utils';
import { ActiveConfigService } from '../active-config/active-config.service';
import { WorkflowConfigService } from '../workflow-config/workflow-config.service';
import { defaultInputWidgetConfig, predefinedFormChildComponents } from './form-config-constants';
import {
  assignParentFormToChildren,
  computeNumberOfInputs,
  removeFieldsFromProjectFormByIds,
} from './form-config.utils';
@Injectable({
  providedIn: 'root',
})
export class WorkflowFormEditorService {
  private readonly workflowConfigService = inject(WorkflowConfigService);
  private readonly activeConfigService = inject(ActiveConfigService);
  private readonly alertHandlerService = inject(AlertHandlerService);

  public getProjectForms(): ProjectFormsConfig[] {
    const workflowConfig = this.workflowConfigService.getLatestConfig();
    return workflowConfig.permissionSections.projectForms ?? [];
  }

  private getExistingFormWidgetChildComponents(
    config: WorkflowConfig,
    formId: string,
  ): NewWorkflowWidgetConfig[] | undefined {
    if (!config?.permissionSections?.roles || !formId) return undefined;

    const { activeDevice } = this.activeConfigService.activeState;

    const normalizedFormId = formatNameAsUrl(formId);

    const findInWidgets = (
      widgets: NewWorkflowWidgetConfig[] | undefined,
    ): NewWorkflowWidgetConfig[] | undefined => {
      if (!Array.isArray(widgets)) return undefined;
      for (const widget of widgets) {
        const widgetFormId = widget?.widgetConfiguration?.attributes?.widgetProperties?.formsConfig?.formID;
        if (widgetFormId && formatNameAsUrl(widgetFormId) === normalizedFormId && widget.childComponents) {
          return widget.childComponents as NewWorkflowWidgetConfig[];
        }

        if (widget.childComponents) {
          if (Array.isArray(widget.childComponents[0])) {
            for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
              const result = findInWidgets(childArray);
              if (result) return result;
            }
          } else {
            const result = findInWidgets(widget.childComponents as NewWorkflowWidgetConfig[]);
            if (result) return result;
          }
        }
      }
      return undefined;
    };

    for (const role of config.permissionSections.roles) {
      if (!role?.zones) continue;
      for (const zone of Object.values(role.zones)) {
        const subscription = zone?.canvases?.subscription;
        if (!subscription) continue;

        for (const zoneDeviceConfig of Object.values(subscription)) {
          const zoneInfo = zoneDeviceConfig as WorkflowZoneDevicesConfig;
          const pages = zoneInfo?.[activeDevice]?.pages ?? [];
          for (const page of pages) {
            let matched = findInWidgets(page.widgets);
            if (matched) return matched;
            matched = findInWidgets(page.footerWidgets);
            if (matched) return matched;
          }
        }
      }
    }

    return undefined;
  }

  public findFieldsConfigByWidgetId(
    widgetId: string,
    newFormId: string,
    projectForms: ProjectFormsConfig[],
  ): ProjectFormsConfig | null | undefined {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    const workflowConfig = this.workflowConfigService.getLatestConfig();

    const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
    const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
      ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;
    if (role) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

      const widget = findWidget(widgetId, widgets);
      return this.modifyProjectFormsFieldsConfig(widget, projectForms, newFormId);
    }
    return null;
  }

  public addFormWidgetAndNewFormConfigInProject(
    formConfigData: ProjectFormsConfig,
    formWidgetConfig?: WorkflowWidgetConfig,
    placementContext?: FormWidgetPlacementContext,
  ) {
    const { activeDevice, activePageIndex, activeRole } = this.activeConfigService.activeState;
    this.workflowConfigService.updateConfig((config) => {
      const role = config.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = getZoneConfig(config, this.activeConfigService.activeState);
      const existingProjectForms: ProjectFormsConfig[] = config.permissionSections?.projectForms ?? [];

      const existingForm = findFormRecursive(
        existingProjectForms,
        formConfigData.id,
        formConfigData.parentFormID,
      );

      const cloneForm = (f: ProjectFormsConfig) => JSON.parse(JSON.stringify(f)) as ProjectFormsConfig;

      // Early guard: if no widget config is provided, only ensure form is added then return.
      if (!formWidgetConfig) {
        if (!existingForm) {
          config.permissionSections.projectForms = [...existingProjectForms, cloneForm(formConfigData)];
        }
        return config; // early return flattens subsequent logic
      }

      // Ensure widgetConfiguration -> attributes -> widgetProperties -> formsConfig exists
      formWidgetConfig.widgetConfiguration ??= {
        attributes: {},
      } as WorkflowWidgetConfig['widgetConfiguration'];
      formWidgetConfig.widgetConfiguration.attributes ??= {} as UiZoneWidgetAttributeConfig;
      const attributes = formWidgetConfig.widgetConfiguration.attributes;
      if (!attributes.widgetProperties) {
        attributes.widgetProperties = {} as UiZoneWidgetAttributeConfig['widgetProperties'];
      }
      attributes.widgetProperties.formsConfig ??= {} as FormsConfigType;
      const formsConfig = attributes.widgetProperties.formsConfig;

      // set default type if not present
      formsConfig.formType = formsConfig.formType || 'root';

      // If incoming formConfigData.id matches a predefined FormID, use mapped child components.
      const isPredefined = Object.values(FormID).includes(formConfigData.id as FormID);
      if (isPredefined) {
        const mapped = predefinedFormChildComponents[formConfigData.id as string];

        // If there are predefined child components mapped to this FormID, use them. Otherwise, fall back to default single input widget.
        const defaultInputWidgetConfigClone = this.cloneDefaultInputWidgetConfigFromExistingForm(
          existingProjectForms,
          existingForm,
          formConfigData.id,
        );

        (formWidgetConfig as WorkflowWidgetConfig).childComponents =
          Array.isArray(mapped) && mapped.length
            ? JSON.parse(JSON.stringify(mapped))
            : JSON.parse(JSON.stringify(defaultInputWidgetConfigClone));
      } else {
        const existingChildComponents = this.getExistingFormWidgetChildComponents(config, formConfigData.id);
        // If this is an existing form and we can find another widget using the same formID,
        // reuse that widget's childComponents as the source of truth.
        if (existingChildComponents && existingChildComponents.length > 0) {
          (formWidgetConfig as WorkflowWidgetConfig).childComponents = JSON.parse(
            JSON.stringify(existingChildComponents),
          );
        } else {
          // existing behavior: ensure there is at least a default child component (flat array)
          const currentChildren = (formWidgetConfig as WorkflowWidgetConfig).childComponents;
          if (!currentChildren || (Array.isArray(currentChildren) && currentChildren.length === 0)) {
            (formWidgetConfig as WorkflowWidgetConfig).childComponents = JSON.parse(
              JSON.stringify(defaultInputWidgetConfig),
            );
          } else {
            // if there are existing children, deep-clone them to avoid mutating upstream references
            (formWidgetConfig as WorkflowWidgetConfig).childComponents = JSON.parse(
              JSON.stringify(currentChildren),
            );
          }
        }
      }

      assignParentFormToChildren(
        (formWidgetConfig as WorkflowWidgetConfig).childComponents,
        formConfigData.id,
      );

      // compute number of inputs and store as string
      const numberOfInputs = computeNumberOfInputs(
        (formWidgetConfig as WorkflowWidgetConfig).childComponents,
      );
      formsConfig.numberOfInputs = numberOfInputs;

      // Insert into captured drop target when available (e.g., specific accordion index),
      // otherwise keep the existing behavior of appending to active page root widgets.
      const targetPageIndex = placementContext?.pageIndex ?? activePageIndex;
      const activePage = role && zoneConfig?.[activeDevice]?.pages?.[targetPageIndex];
      if (activePage) {
        const shouldInsertIntoTarget =
          placementContext?.targetParentId !== undefined && placementContext?.targetParentId !== null;

        if (shouldInsertIntoTarget) {
          this.addFormWidgetToTargetPlacement(
            activePage,
            formWidgetConfig as WorkflowWidgetConfig,
            placementContext,
          );
        } else {
          activePage.widgets.push(formWidgetConfig);
        }
      }

      // if form does not exist, add it to project
      if (!existingForm) {
        // clone formConfigData to avoid mutating the original object passed in and ensure any nested objects are also cloned
        const form = cloneForm(formConfigData);
        form.fieldsConfig = form.fieldsConfig || [];

        this.addInputsConfigToFormWidget(
          numberOfInputs,
          formWidgetConfig as WorkflowWidgetConfig,
          form,
          false,
        );

        const forms = [...existingProjectForms, form];

        config.permissionSections.projectForms = forms;
      } else if (
        existingForm &&
        formWidgetConfig &&
        (!formConfigData.fieldsConfig || formConfigData.fieldsConfig.length === 0)
      ) {
        // Form already exists and formConfigData has empty fieldsConfig
        // Generate fieldsConfig from childComponents and update existing form without duplicating

        // Only update fieldsConfig if it's empty
        if (!existingForm.fieldsConfig || existingForm.fieldsConfig.length === 0) {
          existingForm.fieldsConfig = [];

          this.addInputsConfigToFormWidget(
            numberOfInputs,
            formWidgetConfig as WorkflowWidgetConfig,
            existingForm,
            false,
          );
        }
      }
      return config;
    });
  }

  private addFormWidgetToTargetPlacement(
    activePage: WorkflowPageConfig | undefined,
    formWidgetConfig: WorkflowWidgetConfig,
    placementContext: FormWidgetPlacementContext,
  ) {
    try {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(activePage, placementContext?.widgetLayoutType);

      addWidget(
        formWidgetConfig,
        placementContext?.targetParentId,
        widgets,
        placementContext?.targetIndex,
        placementContext?.target2dChildComponentIndex,
        false,
      );
    } catch (err) {
      if (err instanceof NotLayoutWidgetErr) {
        this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'Cannot add widget below non layout widget');
      } else {
        throw err;
      }
    }
  }

  private cloneDefaultInputWidgetConfigFromExistingForm(
    existingProjectForms: ProjectFormsConfig[],
    existingForm: ProjectFormsConfig | undefined,
    parentFormId: string,
  ): NewWorkflowWidgetConfig[] {
    const defaultInputWidgetConfigClone = JSON.parse(
      JSON.stringify(defaultInputWidgetConfig),
    ) as NewWorkflowWidgetConfig[];

    if (existingForm?.fieldsConfig?.length) {
      const startInputIndex = existingForm.fieldsConfig.length + 1;
      defaultInputWidgetConfigClone[0].widgetConfiguration.attributes.widgetProperties['inputFieldConfig'] = {
        id: `input-${startInputIndex}`,
        type: 'text',
        label: `Input ${startInputIndex}`,
        parentForm: parentFormId,
        name: `input-${startInputIndex}`,
        validation: {
          required: true,
        },
      };
      existingForm.fieldsConfig.push({
        id: `input-${startInputIndex}`,
        type: 'text',
        label: `Input ${startInputIndex}`,
        name: `input-${startInputIndex}`,
      });
    }

    return defaultInputWidgetConfigClone;
  }

  /**
   * Modifies the input fields of a form widget identified by its unique ID within the current workflow configuration.
   *
   * Updates the widget's input fields based on the provided forms configuration. If a previous form was selected,
   * moves input fields from the previous form to the currently selected form. Displays a warning alert if the widget is not found.
   *
   * @param uniqueId - The unique identifier of the form widget to modify.
   * @param formsConfig - The new configuration for the form's inputs.
   * @param previousSelectedFormId - (Optional) The ID of the previously selected form, if applicable.
   */
  public modifyFormWidgets(uniqueId: string, formsConfig: FormsConfigType, previousSelectedFormId?: string) {
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;
      const projectForms: ProjectFormsConfig[] = workflowConfig.permissionSections.projectForms ?? [];
      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          const oldParentFormID =
            widget.widgetConfiguration.attributes.widgetProperties['formsConfig']?.parentFormID;
          this.addOrRemoveInputsFromFormWidget(widget, projectForms, formsConfig);
          this.updateChildComponentsParentForm(widget);

          // If a previous form was selected, modify the fields configuration
          // to move input fields from the previous form to the currently selected form.
          if (previousSelectedFormId?.length) {
            this.modifyProjectFormsFieldsConfig(
              widget,
              projectForms,
              previousSelectedFormId,
              oldParentFormID,
            );
          }
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found');
        }
      }

      return workflowConfig;
    });
  }

  /**
   * Updates the parentForm property of child components in the form widget to match the formID in formsConfig.
   * @param widget The workflow widget configuration.
   */
  private updateChildComponentsParentForm(widget: NewWorkflowWidgetConfig) {
    if (!widget?.childComponents) return;
    (widget?.childComponents as NewWorkflowWidgetConfig[])?.forEach((child) => {
      const parentForm =
        widget?.widgetConfiguration?.attributes?.widgetProperties?.['formsConfig']?.formID ??
        child.widgetConfiguration.attributes.widgetProperties.inputFieldConfig?.parentForm;
      const childFormKey =
        widget?.widgetConfiguration?.attributes?.widgetProperties?.['formsConfig']?.childKey ??
        child.widgetConfiguration.attributes.widgetProperties.inputFieldConfig?.['childFormKey'];
      const formType =
        widget?.widgetConfiguration?.attributes?.widgetProperties?.['formsConfig']?.formType ??
        child.widgetConfiguration.attributes.widgetProperties.inputFieldConfig?.['formType'];
      child.widgetConfiguration.attributes.widgetProperties['parentForm'] = parentForm;
      if (childFormKey) {
        child.widgetConfiguration.attributes.widgetProperties['childFormKey'] = childFormKey;
      }
      child.widgetConfiguration.attributes.widgetProperties['formType'] = formType ?? 'root';
      if (child.widgetConfiguration.attributes.widgetProperties.inputFieldConfig) {
        child.widgetConfiguration.attributes.widgetProperties.inputFieldConfig.parentForm = parentForm;
        child.widgetConfiguration.attributes.widgetProperties.inputFieldConfig['childFormKey'] = childFormKey;
      }
    });
  }

  private getInputFieldsFromWidgetChildren(
    widget: NewWorkflowWidgetConfig,
  ): ProjectFormsConfig['fieldsConfig'] {
    const childComponents = (widget?.childComponents as NewWorkflowWidgetConfig[]) ?? [];
    const seenIds = new Set<string>();

    return childComponents.reduce<ProjectFormsConfig['fieldsConfig']>((acc, child) => {
      const inputFieldConfig = child?.widgetConfiguration?.attributes?.widgetProperties?.inputFieldConfig;
      if (!inputFieldConfig?.id || seenIds.has(inputFieldConfig.id)) {
        return acc;
      }

      seenIds.add(inputFieldConfig.id);
      acc.push({
        id: inputFieldConfig.id,
        type: inputFieldConfig.type as InputFieldType,
        label: inputFieldConfig.label,
        name: inputFieldConfig.name,
      });

      return acc;
    }, []);
  }

  /**
   * Updates the fields configuration of project forms by moving input fields associated with the given widget
   * from the previously selected form to the currently selected form.
   *
   * @param widget The workflow widget configuration, or null.
   * @param projectForms: Array of project form configurations.
   * @param previousSelectedFormId: The ID of the previously selected form.
   */
  private modifyProjectFormsFieldsConfig(
    widget: NewWorkflowWidgetConfig | null,
    projectForms: ProjectFormsConfig[],
    previousSelectedFormId: string,
    oldParentFormID?: string,
  ) {
    if (!widget || !widget.childComponents) return;
    // Extract input field IDs from the widget's child components
    const widgetChildInputsIds = (widget?.childComponents as NewWorkflowWidgetConfig[])?.map(
      (v) => v?.widgetConfiguration?.attributes.widgetProperties?.['inputFieldConfig']?.id,
    );
    if (widgetChildInputsIds?.length) {
      const currentFormsConfig = widget?.widgetConfiguration?.attributes?.widgetProperties?.['formsConfig'];
      const previousForm = findFormRecursive(projectForms, previousSelectedFormId, oldParentFormID);
      let currentForm = findFormRecursive(
        projectForms,
        currentFormsConfig?.formID ?? '',
        currentFormsConfig?.parentFormID,
      );

      // If the current form doesn't exist in the project forms, add it before attempting to move fields.
      if (!currentForm) {
        const newCurrentForm = {
          id: currentFormsConfig?.formID,
          parentFormID: currentFormsConfig?.parentFormID,
          formType: currentFormsConfig?.formType,
          fieldsConfig: [],
        } as ProjectFormsConfig;
        currentForm = newCurrentForm;
        projectForms.push(newCurrentForm);
      }

      // Move input fields from previous form to current form based on widget's child component input field IDs
      if (previousForm && currentForm) {
        if (!previousForm.fieldsConfig?.length) {
          previousForm.fieldsConfig = this.getInputFieldsFromWidgetChildren(widget);
        }

        const newFieldsToAdd = (previousForm.fieldsConfig ?? [])
          .filter((field) => widgetChildInputsIds.includes(field.id))
          .filter((field) => !currentForm.fieldsConfig.some((existing) => existing.id === field.id));

        // Add inputs to current form with updated IDs
        newFieldsToAdd.forEach((field) => {
          // Add the field to the current form
          currentForm.fieldsConfig.push({
            ...field,
            // update the id of the input before pushing
            id: `input-${currentForm.fieldsConfig.length + 1}`,
          });
        });
      }
      return currentForm;
    }
    return null;
  }

  /**
   * Adds or removes input fields from the form widget based on the number of inputs specified in the formsConfig.
   * @param widget The form widget to modify.
   * @param projectForms The project forms configuration array.
   * @returns The modified form widget with updated input fields and updated project forms.
   */
  private addOrRemoveInputsFromFormWidget(
    widget: NewWorkflowWidgetConfig,
    projectForms: ProjectFormsConfig[],
    formsConfig: FormsConfigType,
  ) {
    if (!formsConfig) return;

    widget.widgetConfiguration.attributes.widgetProperties['formsConfig'] = formsConfig;
    const currentInputCount = computeNumberOfInputs(widget.childComponents);
    const targetInputCount = Number(formsConfig.numberOfInputs ?? 0);
    const form = findFormRecursive(projectForms, formsConfig.formID, formsConfig.parentFormID);

    // Add missing inputs if needed
    if (currentInputCount < targetInputCount) {
      this.addInputsToFormWidget(widget, form, currentInputCount, targetInputCount);
    }

    // Remove excess inputs if needed
    if (currentInputCount > targetInputCount) {
      this.removeInputsFromFormWidget(widget, form, targetInputCount, currentInputCount);
    }
  }

  /**
   * Adds input fields to a form widget and project forms.
   * @param widget The form widget to modify.
   * @param form The associated project form configuration.
   * @param currentCount Current number of input fields.
   * @param targetCount Target number of input fields.
   */
  private addInputsToFormWidget(
    widget: NewWorkflowWidgetConfig,
    form: ProjectFormsConfig | undefined,
    currentCount: number,
    targetCount: number,
  ): void {
    const missingInputs = targetCount - currentCount;

    this.addInputsConfigToFormWidget(missingInputs, widget, form);
  }

  private addInputsConfigToFormWidget(
    inputLength: number,
    widget: NewWorkflowWidgetConfig,
    form: ProjectFormsConfig | undefined,
    isWidgetUpdateNeeded = true,
  ) {
    for (let i = 0; i < inputLength; i++) {
      let numberOfInputs: number = computeNumberOfInputs(widget?.childComponents);

      if (isWidgetUpdateNeeded) {
        const widgetConfigToAdd: NewWorkflowWidgetConfig = JSON.parse(JSON.stringify(inputWidgetConfig));
        widgetConfigToAdd.uniqueId = new UUID().toString();

        numberOfInputs = computeNumberOfInputs(widget?.childComponents);
        // Determine the appropriate index for the new input
        const formArrayToUse = this.getFormArrayLength(form, numberOfInputs);

        // Configure the input field
        widgetConfigToAdd.widgetConfiguration.attributes.widgetProperties['inputFieldConfig'] = {
          id: `input-${(formArrayToUse ?? 0) + 1}`,
          type: 'text',
          label: `Input ${(numberOfInputs ?? 0) + 1}`,
          parentForm: form?.id,
          // default values for testing, to be replaced with actual values
          name: `input-${(formArrayToUse ?? 0) + 1}`,

          validation: {
            required: true,
          },
        } as FormFieldConfig;

        // Add the input to the widget
        (widget.childComponents as NewWorkflowWidgetConfig[])?.push(
          JSON.parse(JSON.stringify(widgetConfigToAdd)),
        );
      }

      // Update the project form configuration if it exists
      if (form) {
        form.fieldsConfig.push({
          id: `input-${(form.fieldsConfig.length ?? 0) + 1}`,
          type: 'text',
          label: `Input ${numberOfInputs}`,
          name: `input-${(form.fieldsConfig.length ?? 0) + 1}`,
        });
      }
    }
  }

  /**
   * Removes excess input fields from a form widget and project forms.
   * @param widget The form widget to modify.
   * @param form The associated project form configuration.
   * @param targetCount Target number of input fields.
   * @param currentCount Current number of input fields.
   */
  private removeInputsFromFormWidget(
    widget: NewWorkflowWidgetConfig,
    form: ProjectFormsConfig | undefined,
    targetCount: number,
    currentCount: number,
  ): void {
    if (!widget?.childComponents || currentCount <= targetCount) return;

    const inputIdsToRemove: string[] = [];
    let remainingToRemove = currentCount - targetCount;

    const removeFromArray = (arr: any[]) => {
      for (let idx = arr.length - 1; idx >= 0 && remainingToRemove > 0; idx--) {
        const item = arr[idx];
        if (Array.isArray(item)) {
          // Recurse into nested arrays (e.g., layout columns)
          removeFromArray(item);
          // continue to next sibling after processing nested array
          continue;
        }
        if (item && typeof item === 'object' && item.name === WidgetNames.INPUT_WIDGET) {
          const id = item?.widgetConfiguration?.attributes?.widgetProperties?.['inputFieldConfig']?.id;
          if (id) inputIdsToRemove.push(id);
          arr.splice(idx, 1);
          remainingToRemove--;
        }
      }
    };

    // Start removal from the top-level childComponents array
    removeFromArray(widget.childComponents as NewWorkflowWidgetConfig[]);

    removeFieldsFromProjectFormByIds(form, inputIdsToRemove);
  }

  /**
   * Gets the appropriate length to use for form array indexing.
   * @param form The project form configuration.
   * @param childComponentsLength The length of child components.
   * If @param form has fieldsConfig, then that should be used.
   * @returns The appropriate length to use for indexing.
   */
  private getFormArrayLength(
    form: ProjectFormsConfig | undefined,
    childComponentsLength: number | undefined,
  ): number {
    return typeof form?.fieldsConfig?.length === 'number' && form?.fieldsConfig?.length > 0
      ? form.fieldsConfig.length
      : (childComponentsLength ?? 0);
  }

  /**
   * Returns a list of input field labels from the current form that are not present on the widget
   * with the specified uniqueId on the active page. If the widget is not found, triggers a warning alert.
   *
   * @param uniqueId - The unique identifier of the widget to search for.
   * @returns An array of input field labels not present on the widget on the other pages.
   */
  public generateListOfInputsOnOtherPages(uniqueId: string): string[] {
    const workflowConfig = this.workflowConfigService.getLatestConfig();
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;
    const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
    const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
      ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;
    const projectForms: ProjectFormsConfig[] = workflowConfig.permissionSections.projectForms ?? [];
    let fieldOnOtherPages: {
      id: string;
      type: InputFieldType;
      label: string;
      name: string;
    }[] = [];
    if (role) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

      const widget = findWidget(uniqueId, widgets);
      if (widget) {
        const form = projectForms.find(
          (form) =>
            form.id === widget.widgetConfiguration.attributes.widgetProperties?.['formsConfig']?.formID,
        );
        if (form) {
          const inputsOnWidget = (widget.childComponents as NewWorkflowWidgetConfig[])?.map(
            (child) => child.widgetConfiguration.attributes?.widgetProperties?.['inputFieldConfig']?.id,
          );

          fieldOnOtherPages = form.fieldsConfig.map((field) => field);
          fieldOnOtherPages = fieldOnOtherPages.filter((field) => !inputsOnWidget?.includes(field.id));
        }
      } else {
        this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found');
      }
    }
    const fieldOnOtherPagesFinal = fieldOnOtherPages.map((field) => field.name);
    return fieldOnOtherPagesFinal;
  }

  public modifyInputWidgets(
    uniqueId: string,
    attributesUpdate: UiZoneWidgetAttributeConfig,
    nestedWidgetsUpdate: {
      [uniqueId: string]: NestedWorkflowWidgetConfiguration;
    },
  ) {
    const inputFieldConfig = attributesUpdate?.widgetProperties.inputFieldConfig;
    const { activeDevice, activeZone, activeRole, activeCanvasSubscription, activePageIndex } =
      this.activeConfigService.activeState;

    this.workflowConfigService.updateConfig((workflowConfig) => {
      const role = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole);
      const zoneConfig = workflowConfig.permissionSections.roles.find((role) => role.name === activeRole)
        ?.zones[activeZone]?.canvases.subscription[activeCanvasSubscription] as WorkflowZoneDevicesConfig;
      const projectForms: ProjectFormsConfig[] = workflowConfig.permissionSections.projectForms ?? [];
      if (role) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const widgets = zoneConfig[activeDevice]?.pages[activePageIndex].widgets!;

        const widget = findWidget(uniqueId, widgets);
        if (widget) {
          widget.widgetConfiguration.attributes = {
            ...widget.widgetConfiguration.attributes,
            ...attributesUpdate,
          };
          widget.widgetConfiguration.nestedWidgets = {
            ...widget.widgetConfiguration.nestedWidgets,
            ...nestedWidgetsUpdate,
          };
          projectForms.find((form) => {
            if (form.id === inputFieldConfig?.parentForm) {
              form.fieldsConfig.find((inputField) => {
                if (inputField.id === inputFieldConfig.id) {
                  inputField.label = inputFieldConfig.label;
                  inputField.name = inputFieldConfig.name;
                  inputField.type = inputFieldConfig.type;
                }
              });
            }
          });
        } else {
          this.alertHandlerService.showAlertFn(AlertTypes.WARNING, 'No widget found');
        }
      }

      return workflowConfig;
    });
  }
}
